/**
 * Custom domains API client (roadmap P1). See docs/canonical/CUSTOM_DOMAINS.md.
 */
import type {
  DomainCertStatus,
  DomainContext,
  DomainDnsCheck,
  DomainJobResponse,
  DomainProvisionJob,
  TenantDomain,
} from '@foxet/shared';
import { request, BASE_URL } from './client.js';

export interface DomainListResponse {
  domains: TenantDomain[];
  /** What the owner has to put in their DNS panel. Served by the API so the UI never hardcodes the IP. */
  dnsTarget: { ip: string; cname: string };
}

export const domainsApi = {
  list() {
    return request<DomainListResponse>('/domains');
  },
  create(data: { hostname: string; productId?: string | null }) {
    return request<TenantDomain>('/domains', { method: 'POST', body: data });
  },
  rebind(id: string, productId: string | null) {
    return request<TenantDomain>(`/domains/${id}`, { method: 'PATCH', body: { productId } });
  },
  /**
   * 204 (nothing on the box owned it) → resolves undefined.
   * 202 (the domain was live) → resolves a job to poll; the card must stay on
   * screen until the runner has actually taken the vhost + cert down.
   */
  remove(id: string) {
    return request<DomainJobResponse | undefined>(`/domains/${id}`, { method: 'DELETE' });
  },
  verifyDns(id: string) {
    return request<{ domain: TenantDomain; dns: DomainDnsCheck }>(`/domains/${id}/verify-dns`, { method: 'POST' });
  },
  /**
   * ENQUEUES — the API cannot run the provisioning scripts itself (it is
   * containerised; a host-side runner does the work). Returns either a `job` to
   * poll or a `result` explaining why nothing was queued (DNS gate, or a job
   * already in flight).
   */
  provision(id: string) {
    return request<DomainJobResponse>(`/domains/${id}/provision`, { method: 'POST' });
  },
  deprovision(id: string) {
    return request<DomainJobResponse>(`/domains/${id}/deprovision`, { method: 'POST' });
  },
  /** Poll target. `domain` comes back null when a completed `remove` job deleted the row. */
  job(domainId: string, jobId: string) {
    return request<{ domain: TenantDomain | null; job: DomainProvisionJob }>(`/domains/${domainId}/jobs/${jobId}`);
  },
  certStatus(id: string) {
    return request<DomainCertStatus>(`/domains/${id}/cert-status`);
  },
};

/**
 * Boot-time Host resolution — deliberately NOT using `request()`.
 *
 * This runs before React mounts, before any auth store exists, and on an
 * origin that may not be a platform host. `request()` would attach auth
 * headers and route 401s through the login redirect; none of that applies to
 * an anonymous visitor on a customer's demo domain.
 *
 * `?host=` is sent explicitly because the dashboard bundle is built with an
 * absolute VITE_API_URL — the Host header the API would otherwise see is
 * api.foxet.ai, not the domain the visitor typed.
 *
 * Any failure (network, 5xx, malformed body) resolves to a MISS, i.e. the app
 * boots as the normal Foxet dashboard. Failing open to a guessed tenant is the
 * one outcome this must never produce.
 */
export async function fetchDomainContext(host: string): Promise<DomainContext> {
  const miss: DomainContext = { host, tenantSlug: null, productSlug: null };
  try {
    const res = await fetch(`${BASE_URL}/public/domain-context?host=${encodeURIComponent(host)}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return miss;
    const body = (await res.json()) as Partial<DomainContext>;
    return {
      host,
      tenantSlug: typeof body?.tenantSlug === 'string' && body.tenantSlug ? body.tenantSlug : null,
      productSlug: typeof body?.productSlug === 'string' && body.productSlug ? body.productSlug : null,
    };
  } catch {
    return miss;
  }
}
